<script lang="ts" setup>
// @ts-ignore
import { Splide } from '@splidejs/vue-splide'
// @ts-ignore
import { SplideSlide } from '@splidejs/vue-splide'
import '@splidejs/vue-splide/css'
import { ref, computed } from 'vue'

export interface WidgetReelsProps {
    data: Reel[]
    title: string
    subtitle: string
    flashLinks: PageHeaderLinkItem[]
}

const props = defineProps<WidgetReelsProps>()

const currentReel = ref(0)
const reelsRef = ref<HTMLElement>()
const videosRef = ref<HTMLVideoElement[]>()
const videosSliderRef = ref<HTMLVideoElement[]>()
const sliderConfig = ref({
    type: 'loop',
    pagination: false,
    arrows: false,
    gap: '10px',
    drag: true,
    perPage: 1,
    snap: true,
    updateOnMove: true,
    padding: '20%',
    mediaQuery: 'min',
})

const handleMouseEnter = (index: number) => {
    // stop first video with autoplay
    if(index > 0) {
        videosRef.value?.at(0)?.pause()
    }
    if(videosRef.value) {
        videosRef.value.at(index)?.play()
    }
}

const handleMouseLeave = (index: number) => {
    if(videosRef.value) {
        videosRef.value.at(index)?.pause()
    }
}

const handleSlideChange = (slide: {index: number}) => {
    if(videosSliderRef.value){
        videosSliderRef.value.at(currentReel.value)?.pause()
        videosSliderRef.value.at(slide.index)?.play()
        currentReel.value = slide.index
    }
}

const isChristmasPeriod = computed((): boolean => {
    const now = new Date()
    const month = now.getMonth() + 1 
    const day = now.getDate()
    
    // December 6 - January 6
    if ((month === 12 && day >= 6) || (month === 1 && day <= 6)) {
        return true
    }
    
    return false
})
</script>
<template>
  <ContainerFluid class="pb-15 pb-lg-20 mb-8 mb-xl-0">
    <div class="text-center mt-8 mt-md-12">
      <h1
        class="mb-1 mx-lg-auto alfa d-inline"
        :class="{ 'title-with-hat': isChristmasPeriod }"
      >
        {{ props.title }}
      </h1>
    </div>
    <h2
      class="fw-bolder text-center delta mb-0"
    >
      {{ props.subtitle }}
    </h2>
    <div
      class="reels-wrapper mt-2 mt-xl-0"
    >
      <Splide
        class="d-md-none"
        :options="sliderConfig"
        @splide:active="handleSlideChange"
      >
        <SplideSlide
          v-for="(reel, index) in props.data"
          :key="`wri-${index}`"
          :has-track="false"
        >
          <div
            class="reel-video"
            :style="`background-image: url('${reel.thumbnail?.['300x0']?.url ?? ''}')`"
            :class="{'is-active': index === currentReel}"
          >
            <video
              ref="videosSliderRef"
              class="d-block img-fluid w-100"
              :poster="reel.thumbnail?.['300x0']?.url"
              preload="none"
              loop
              muted
              playsinline
              :autoplay="index === 0"
              :alt="reel.description"
            >
              <source
                v-for="(file) in reel.files"
                :key="`file-${file.url}`"
                :type="file.type"
                :src="file.url"
              >
            </video>
            <a
              class="reel-link text-white text-decoration-none fs-big fs-md-smaller fs-lg-big position-absolute bottom-0 start-0 d-flex flex-column justify-content-end end-0 p-4"
              :aria-label="`${$t('Prejsť na kategóriu poistení')} ${reel.title}`"
              :href="reel.video_link"
            >
              <div class="d-flex align-items-center gap-2">
                {{ reel.title }}
                <svg
                  class="d-block text-white"
                  width="18"
                  height="8"
                  aria-hidden="true"
                >
                  <use xlink:href="#arrow-right" />
                </svg>
              </div>
            </a>
          </div>
        </SplideSlide>
      </Splide>
      <div
        ref="reelsRef"
        class="reels"
      >
        <div
          v-for="(reel, index) in props.data"
          :key="`reel-${index}${reel.id}`"
          class="reel"
          @mouseenter="handleMouseEnter(index)"
          @mouseleave="handleMouseLeave(index)"
        >
          <div
            class="reel-video"
            :style="`background-image: url('${reel.thumbnail?.['300x0']?.url ?? ''}')`"
            :class="{'is-active': index === currentReel}"
          >
            <video
              ref="videosRef"
              :poster="reel.thumbnail?.['300x0']?.url"
              preload="none"
              loop
              muted
              playsinline
              :autoplay="index === 0"
              :alt="reel.description"
            >
              <source
                v-for="(file) in reel.files"
                :key="`file-${file.url}`"
                :type="file.type"
                :src="file.url"
              >
            </video>
            <a
              class="reel-link text-white text-decoration-none fs-smaller fs-lg-big position-absolute bottom-0 start-0 d-flex flex-column justify-content-end end-0 p-4"
              :aria-label="`${$t('Prejsť na kategóriu poistení:')} ${reel.title}`"
              :href="reel.video_link"
            >
              <div class="d-flex align-items-center gap-2">
                {{ reel.title }}
                <svg
                  class="d-block text-white"
                  width="18"
                  height="8"
                  aria-hidden="true"
                >
                  <use xlink:href="#arrow-right" />
                </svg>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  </ContainerFluid>
  <PageHeaderLinks
    v-if="props.flashLinks"
    :data="props.flashLinks"
  />
  <IconsWrapper>
    <IconsSymbolAngleLeft />
    <IconsSymbolAngleRight />
    <IconsSymbolArrowRight />
  </IconsWrapper>
</template>
<style lang="scss">
@import './WidgetReels.scss'
</style>
